import type { DefaultAutomationTemplateMetadata } from '@reach/shared-types/automation-template';
import React, { useState } from 'react';
import { TemplateDialogShell, TemplateNameAndDescriptionDialog } from '../template-dialog-helpers';
import { BaseSelection } from './BaseSelection';
import { useCreatePartnerAutomationFromDefault } from './useCreateTemplate';

interface CreateTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTemplateCreated: (templateId: string) => void;
  defaultTemplates?: DefaultAutomationTemplateMetadata[];
}

type DialogStep = 'selection' | 'customize';

export const CreateTemplateDialog: React.FC<CreateTemplateDialogProps> = ({
  open,
  onOpenChange,
  onTemplateCreated,
  defaultTemplates = [],
}) => {
  const [step, setStep] = useState<DialogStep>('selection');
  const [selectedTemplateName, setSelectedTemplateName] = useState<string | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');
  const createFromDefaultMutation = useCreatePartnerAutomationFromDefault();

  const handleClose = () => {
    setStep('selection');
    setSelectedTemplateName(null);
    setTemplateName('');
    setDescription('');
    onOpenChange(false);
  };

  const handleSelectTemplate = (templateName: string) => {
    setSelectedTemplateName(templateName);
    setStep('customize');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const { created, templateId } = await createFromDefaultMutation.mutateAsync({
      defaultTemplateName: selectedTemplateName!,
      customName: templateName,
      customDescription: description,
    });

    if (created && templateId) {
      setTemplateName('');
      setDescription('');
      handleClose();
      onTemplateCreated(templateId);
    }
  };

  const handleBack = () => {
    setStep('selection');
    setSelectedTemplateName(null);
    setTemplateName('');
    setDescription('');
  };

  if (!open) return null;

  if (step === 'selection') {
    return (
      <TemplateDialogShell onClose={handleClose}>
        <BaseSelection
          templates={defaultTemplates}
          onSelectTemplate={handleSelectTemplate}
          onClose={handleClose}
        />
      </TemplateDialogShell>
    );
  }

  return (
    <TemplateNameAndDescriptionDialog
      templateName={templateName}
      description={description}
      onTemplateNameChange={setTemplateName}
      onDescriptionChange={setDescription}
      onSubmit={handleSubmit}
      onCancel={handleBack}
      onClose={handleClose}
      isLoading={createFromDefaultMutation.isPending}
      title="Customize Template"
      submitLabel="Create Template"
      submittingLabel="Creating..."
      cancelLabel="Back"
    />
  );
};
